import Aura from '@primeuix/themes/aura'
import { definePreset } from '@primeuix/themes'

/**
 * The "Console" design system (see docs/UI-GUIDELINES.md) expressed as a PrimeVue
 * preset, so every PrimeVue component in the panel takes the Console look without
 * per-screen CSS.
 *
 * Values point at the CSS variables of src/styles/tokens.css wherever they can,
 * so the token file stays the one place a colour or a radius is defined. The
 * neutral scale below is the one exception: PrimeVue derives hover and border
 * shades from `surface.*` steps, which have to be opaque colours, so they are the
 * Console ink alphas flattened onto white (and, for the dark theme, onto #1c1c1c).
 */

/** A status tag: the hue as text on a 12% wash of itself — never bordered, never saturated. */
function statusTag(hue: string, text = hue) {
  return { background: `color-mix(in srgb, ${hue} 12%, transparent)`, color: text }
}

/** One colour map for both themes: its values are tokens, which switch by themselves. */
function both<T>(scheme: T) {
  return { light: scheme, dark: scheme }
}

/**
 * The colours of both themes. They are the token names, so the one map serves
 * light and dark alike: tokens.css swaps the values under `data-theme="dark"`.
 */
const SCHEME = {
  highlight: {
    background: 'var(--surface-selected)',
    focusBackground: 'var(--surface-active)',
    color: 'var(--ink-1000)',
    focusColor: 'var(--ink-1000)',
  },
  mask: { background: 'var(--scrim)', color: 'var(--ink-1000)' },
  formField: {
    background: 'var(--white)',
    disabledBackground: 'var(--surface-subtle)',
    filledBackground: 'var(--gray-050)',
    filledHoverBackground: 'var(--gray-050)',
    filledFocusBackground: 'var(--gray-050)',
    borderColor: 'var(--border-field)',
    hoverBorderColor: 'var(--border-strong)',
    focusBorderColor: 'var(--border-strong)',
    invalidBorderColor: 'var(--alert-red)',
    color: 'var(--text-primary)',
    disabledColor: 'var(--control-disabled-fg)',
    placeholderColor: 'var(--text-tertiary)',
    floatLabelColor: 'var(--text-secondary)',
    floatLabelFocusColor: 'var(--text-primary)',
    floatLabelActiveColor: 'var(--text-secondary)',
    iconColor: 'var(--text-secondary)',
    shadow: 'none',
  },
  text: {
    color: 'var(--text-primary)',
    hoverColor: 'var(--text-primary)',
    mutedColor: 'var(--text-secondary)',
    hoverMutedColor: 'var(--ink-700)',
  },
  content: {
    background: 'var(--surface-page)',
    hoverBackground: 'var(--surface-hover)',
    borderColor: 'var(--border-hairline)',
    color: 'var(--text-primary)',
    hoverColor: 'var(--text-primary)',
  },
  overlay: {
    select: { background: 'var(--surface-overlay)', borderColor: 'transparent', color: 'var(--text-primary)' },
    popover: { background: 'var(--surface-overlay)', borderColor: 'transparent', color: 'var(--text-primary)' },
    modal: { background: 'var(--surface-overlay)', borderColor: 'transparent', color: 'var(--text-primary)' },
  },
  list: {
    option: {
      focusBackground: 'var(--surface-hover)',
      selectedBackground: 'var(--surface-selected)',
      selectedFocusBackground: 'var(--surface-active)',
      color: 'var(--text-primary)',
      focusColor: 'var(--text-primary)',
      selectedColor: 'var(--text-primary)',
      selectedFocusColor: 'var(--text-primary)',
      icon: { color: 'var(--text-secondary)', focusColor: 'var(--text-primary)' },
    },
    optionGroup: { background: 'transparent', color: 'var(--text-secondary)' },
  },
  navigation: {
    item: {
      focusBackground: 'var(--surface-hover)',
      activeBackground: 'var(--surface-selected)',
      color: 'var(--text-primary)',
      focusColor: 'var(--text-primary)',
      activeColor: 'var(--text-primary)',
      icon: { color: 'var(--ink-700)', focusColor: 'var(--ink-1000)', activeColor: 'var(--ink-1000)' },
    },
    submenuLabel: { background: 'transparent', color: 'var(--text-secondary)' },
    submenuIcon: { color: 'var(--text-tertiary)', focusColor: 'var(--text-secondary)', activeColor: 'var(--text-secondary)' },
  },
}

export const ConsolePreset = definePreset(Aura, {
  primitive: {
    borderRadius: { none: '0', xs: '2px', sm: '4px', md: '8px', lg: '8px', xl: '12px' },
  },
  semantic: {
    transitionDuration: '140ms',
    focusRing: { width: '0', style: 'none', color: 'transparent', offset: '0', shadow: 'var(--ring-focus)' },
    primary: {
      50: '#f4f4f4',
      100: '#ebebeb',
      200: '#d5d5d5',
      300: '#b3b3b3',
      400: '#828282',
      500: '#1c1c1c',
      600: '#161616',
      700: '#111111',
      800: '#0b0b0b',
      900: '#050505',
      950: '#000000',
    },
    formField: {
      paddingX: '0.75rem',
      paddingY: '0.375rem',
      sm: { fontSize: '0.8125rem', paddingX: '0.625rem', paddingY: '0.25rem' },
      lg: { fontSize: '0.875rem', paddingX: '0.875rem', paddingY: '0.625rem' },
      borderRadius: 'var(--radius-field)',
      transitionDuration: 'var(--duration-fast)',
    },
    content: { borderRadius: 'var(--radius-8)' },
    navigation: {
      list: { padding: '0', gap: 'var(--space-2)' },
      item: { padding: '0 var(--space-8)', borderRadius: 'var(--radius-control)', gap: 'var(--space-8)' },
    },
    overlay: {
      select: { borderRadius: 'var(--radius-panel)', shadow: 'var(--shadow-popover)' },
      popover: { borderRadius: 'var(--radius-panel)', padding: 'var(--space-12)', shadow: 'var(--shadow-popover)' },
      modal: { borderRadius: 'var(--radius-panel)', padding: 'var(--card-padding)', shadow: 'var(--shadow-dialog)' },
      navigation: { shadow: 'var(--shadow-popover)' },
    },
    colorScheme: {
      light: {
        ...SCHEME,
        surface: {
          0: '#ffffff',
          50: '#f7f9fb',
          100: '#f1f2f4',
          200: '#e7e9ec',
          300: '#dadbdd',
          400: '#a4a4a4',
          500: '#828282',
          600: '#494949',
          700: '#333333',
          800: '#262626',
          900: '#1c1c1c',
          950: '#111111',
        },
        primary: { color: 'var(--ink-1000)', contrastColor: 'var(--white)', hoverColor: '#333333', activeColor: '#000000' },
      },
      // The dark theme: the same scale run the other way — Aura's dark scheme
      // reads its backgrounds from the dark end (900 is the page, #1c1c1c).
      dark: {
        ...SCHEME,
        surface: {
          0: '#ffffff',
          50: '#f5f5f5',
          100: '#e7e7e7',
          200: '#cfcfcf',
          300: '#a4a4a4',
          400: '#828282',
          500: '#5f5f5f',
          600: '#494949',
          700: '#3a3a3a',
          800: '#2a2a2a',
          900: '#1c1c1c',
          950: '#141414',
        },
        primary: { color: 'var(--ink-1000)', contrastColor: 'var(--white)', hoverColor: '#e0e0e0', activeColor: '#cccccc' },
      },
    },
  },
  components: {
    button: {
      root: {
        label: { fontWeight: '500' },
        iconOnlyWidth: 'var(--control-height)',
        sm: { iconOnlyWidth: 'var(--control-height-sm)' },
        raisedShadow: 'none',
      },
      colorScheme: both({
        root: {
          secondary: {
            background: 'var(--control-secondary-bg)',
            hoverBackground: 'var(--ink-200)',
            activeBackground: 'var(--ink-200)',
            borderColor: 'transparent',
            hoverBorderColor: 'transparent',
            activeBorderColor: 'transparent',
            color: 'var(--control-secondary-fg)',
            hoverColor: 'var(--control-secondary-fg)',
            activeColor: 'var(--control-secondary-fg)',
          },
        },
        outlined: {
          primary: { hoverBackground: 'var(--surface-hover)', activeBackground: 'var(--surface-active)', borderColor: 'var(--border-hairline)', color: 'var(--ink-1000)' },
          secondary: { hoverBackground: 'var(--surface-hover)', activeBackground: 'var(--surface-active)', borderColor: 'var(--border-hairline)', color: 'var(--ink-1000)' },
        },
        text: {
          primary: { hoverBackground: 'var(--surface-hover)', activeBackground: 'var(--surface-active)', color: 'var(--control-ghost-fg)' },
          secondary: { hoverBackground: 'var(--surface-hover)', activeBackground: 'var(--surface-active)', color: 'var(--control-ghost-fg)' },
        },
      }),
    },
    // The default card is Console's bordered variant — white on a hairline, no
    // drop shadow: nearly every card here holds a table or a form. Metric tiles
    // use the flat tints instead (docs/UI-GUIDELINES.md §3).
    card: {
      root: {
        background: 'var(--surface-raised)',
        borderRadius: 'var(--radius-card)',
        color: 'var(--text-primary)',
        shadow: 'var(--shadow-hairline)',
      },
      body: { padding: 'var(--card-padding)', gap: 'var(--space-16)' },
      title: { fontSize: 'var(--fs-section)', fontWeight: '600' },
      subtitle: { color: 'var(--text-secondary)' },
    },
    tag: {
      root: {
        fontSize: 'var(--fs-small)',
        fontWeight: '400',
        padding: '1px 6px',
        gap: 'var(--space-4)',
        borderRadius: 'var(--radius-tag)',
        roundedBorderRadius: 'var(--radius-pill)',
      },
      colorScheme: both({
        // `primary` on a tag means "in progress" here — see src/api/status.ts.
        primary: statusTag('var(--status-running)'),
        secondary: statusTag('var(--status-idle)', 'var(--ink-600)'),
        success: statusTag('var(--status-done)'),
        info: statusTag('var(--status-delivered)'),
        warn: statusTag('var(--status-attention)', 'var(--status-attention-fg)'),
        danger: statusTag('var(--status-failed)'),
        contrast: { background: 'var(--ink-1000)', color: 'var(--white)' },
      }),
    },
    message: {
      root: { borderRadius: 'var(--radius-panel)', borderWidth: '0' },
      text: { fontSize: 'var(--fs-body)', fontWeight: '500' },
      colorScheme: both({
        info: { background: 'var(--info-bg)', borderColor: 'transparent', color: 'var(--info-fg)', shadow: 'none' },
        success: { background: 'var(--tint-mint)', borderColor: 'transparent', color: 'var(--ink-1000)', shadow: 'none' },
        warn: { background: 'var(--tint-butter)', borderColor: 'transparent', color: 'var(--ink-1000)', shadow: 'none' },
        error: { background: 'color-mix(in srgb, var(--alert-red) 10%, transparent)', borderColor: 'transparent', color: 'var(--alert-red)', shadow: 'none' },
        secondary: { background: 'var(--surface-card)', borderColor: 'transparent', color: 'var(--ink-700)', shadow: 'none' },
      }),
    },
    datatable: {
      // Opaque, also when sorted or hovered: frozen and sticky header cells cover the columns scrolling under them.
      headerCell: {
        padding: 'var(--space-10) var(--space-12)',
        color: 'var(--text-secondary)',
        background: 'var(--surface-page)',
        hoverBackground: 'var(--surface-subtle)',
        selectedBackground: 'var(--surface-subtle)',
        selectedColor: 'var(--text-primary)',
      },
      columnTitle: { fontWeight: '400' },
      bodyCell: { padding: 'var(--space-12)' },
      row: { hoverBackground: 'var(--surface-hover)', selectedBackground: 'var(--surface-selected)' },
    },
    togglebutton: {
      root: { padding: '0', borderRadius: 'var(--radius-control)', fontWeight: '400' },
      content: { padding: '0 var(--space-10)', borderRadius: 'var(--radius-control)', checkedShadow: 'none' },
      colorScheme: both({
        root: {
          background: 'transparent',
          checkedBackground: 'transparent',
          hoverBackground: 'var(--surface-hover)',
          borderColor: 'transparent',
          checkedBorderColor: 'transparent',
          color: 'var(--text-secondary)',
          hoverColor: 'var(--text-primary)',
          checkedColor: 'var(--text-primary)',
        },
        content: { checkedBackground: 'var(--surface-selected)' },
        icon: { color: 'var(--text-secondary)', hoverColor: 'var(--text-primary)', checkedColor: 'var(--text-primary)' },
      }),
    },
    menu: {
      root: { background: 'transparent', borderColor: 'transparent', shadow: 'none' },
    },
    // The detail drawer: Console's right sheet — white, hairline edge, dialog
    // shadow over a 24%-ink scrim (docs/UI-GUIDELINES.md §6).
    drawer: {
      root: { background: 'var(--surface-overlay)', borderColor: 'var(--border-hairline)', shadow: 'var(--shadow-dialog)' },
      header: { padding: 'var(--space-20) var(--card-padding)' },
      title: { fontSize: 'var(--fs-title)', fontWeight: '600' },
      content: { padding: '0 var(--card-padding) var(--card-padding)' },
      footer: { padding: 'var(--space-16) var(--card-padding)' },
    },
  },
})

/**
 * The colour of a tone, for surfaces that are not a Tag — status dots and cells. Same hues as the tag preset above.
 */
export const TONE_COLOR = {
  success: 'var(--status-done)',
  info: 'var(--status-delivered)',
  primary: 'var(--status-running)',
  warn: 'var(--status-attention)',
  danger: 'var(--status-failed)',
  secondary: 'var(--status-idle)',
} as const
